import React, { useEffect, useRef, useState } from 'react';
import Switch from 'react-switch';

import { Api } from '@apis/client';
import Http from '@helpers/Http';
import Filter from '@helpers/Filter';
import FridayService from '@services/friday';

import './index.module.less';

import PullRefresher from '@components/PullRefresher';
import DragRefresher from '@components/DragRefresher';
import NavBar from '@components/NavBar';
import PopupMenu from '@widgets/PopupMenu';
import ConfirmModal from '@views/components/ConfirmModal';
import Toast from '@widgets/Toast';
import Item from './Item';


import emptyUrl from '@images/empty/empty_normal.png';

const actionPopupMenuList = [
  {
    title: '清空所有短信',
    value: 1,
    icon: 'icon-delete',
  },
];

const DeviceMessages = () => {
  const params = useRef(Filter.urlParams(window.location.search));
  const actionAnchorRef = useRef<HTMLDivElement>(null);
  const [switchStatus, setSwitchStatus] = useState(false);
  const PullRefresherComponent = useRef<PullRefresher>(null);
  const DragRefresherComponent = useRef<DragRefresher>(null);
  const viewId = useRef('view');
  const viewContainerId = useRef(viewId.current + 'Container');

  const initCondition = {
    start: 1,
    pageSize: 10,
  };
  const [dataCondition, setDataCondition] = useState(initCondition);
  const [pagination, setPagination] = useState<{
    list: null | any[];
    totalPage: number;
  }>({ list: null, totalPage: 0 });

  const getInitMessage = async () => {
    const { data } = await Http.post(Api.GetDeviceReceiveMessageConfig, {
      snCode: params.current.sncode,
      company: 'lingge',
    });

    if (data?.isReceiveMessage) {
      setSwitchStatus(true);
    }
    const { data: result } = await Http.post(Api.GetDeviceReceiveMessages, {
      snCode: params.current.sncode,
      company: 'lingge',
      start: dataCondition.start,
      pageSize: dataCondition.pageSize,
    });
    setPagination({
      list: result.result,
      totalPage: Math.ceil(result.totalCount / dataCondition.pageSize),
    });
  };

  const changeConfig = async (checked: boolean) => {
    FridayService.addEvent('receiving_message_receiving_message_switch_click', { type: checked ? '开' : '关' });
    setSwitchStatus(checked);
    await Http.post(Api.PostDeviceReceiveMessageConfig, {
      snCode: params.current.sncode,
      company: 'lingge',
      isReceiveMessage: checked,
    });
  };

  const getMessageLists = async (extra = {}, reset?: boolean) => {
    const condition = {
      snCode: params.current.sncode,
      company: 'lingge',
      ...dataCondition,
      ...extra,
    };
    let list = pagination.list || [];
    if (reset) {
      list = [];
      condition.start = 1;
    }

    const { data } = await Http.post(Api.GetDeviceReceiveMessages, condition);
    const resultList = [...list, ...data.result];
    setPagination({
      list: resultList,
      totalPage: Math.ceil(data.totalCount / initCondition.pageSize),
    });
    setDataCondition(condition);
    return resultList;
  };

  const refreshMessages = async () => {
    const { data } = await Http.post(Api.GetDeviceReceiveMessages, {
      snCode: params.current.sncode,
      company: 'lingge',
      ...initCondition,
    });
    setDataCondition(initCondition);
    setPagination({
      list: data.result,
      totalPage: Math.ceil(data.totalCount / initCondition.pageSize),
    });
    DragRefresherComponent.current?.complete();
  };

  const loadMore = async () => {
    await getMessageLists({ start: dataCondition.start + 1 });
    PullRefresherComponent?.current?.reset();
  };

  const deleteMessage = async (uid: string) => {
    FridayService.addEvent('receiving_message_delete_message_delete_button_click');
    await Http.post(Api.DeleteDeviceMessage, {
      msgIds: [uid],
    });
    Toast.show({
      title: '删除成功',
      type: 'strip',
    });
    refreshMessages();

  };

  const deleteAllMessage = async () => {
    FridayService.addEvent('receiving_message_clear_all_message_clear_button_click');
    await Http.post(Api.DeleteDeviceAllMessages, {
      snCode: params.current.sncode,
      company: 'lingge',
    });
    Toast.show({
      title: '删除成功',
      type: 'strip'
    });
    refreshMessages();
  };


  const clearMessages = () => {
    if(!pagination.list?.length) {
      return Toast.show({
        title: '没有可以清空的短信列表',
        type: 'strip-bottom'

      })
    }
    PopupMenu.show({
      width: 160,
      items: actionPopupMenuList,
      anchor: actionAnchorRef,
      onTap(_, value) {
        ConfirmModal.show({
          title: '确认清空所有短信？',
          text: '清空后，短信将无法恢复。是否清空所有短信？',
          confirmTheme: 'warn',
          showCancelBtn: true,
          confirmBtnText: '清空',
          onConfirm: () => {
            deleteAllMessage();
          },
        });
      },
    });
  }

  useEffect(() => {
    getInitMessage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderMessages = () => {
    let Items = null;
    if (pagination?.list?.length) {
      Items = pagination?.list.map((item, index) => {
        return <Item item={item} onTap={deleteMessage} key={index} />;
      });
    }
    return Items;
  };

  return (
    <div className="flex container" styleName="container">
      <NavBar
        title="代收短信"
        RightBtnComponent={
          <i
            className="iconfont icon-icon-more"
            style={{ marginRight: '.24rem', fontSize: '.4rem' }}
            onClick={clearMessages}
            ref={actionAnchorRef}
          />
        }
      />
      <div styleName="view-content" id={viewContainerId.current}>
        <DragRefresher
          scrollerId={viewId.current}
          draggerId={viewContainerId.current}
          onRefresh={refreshMessages}
          ref={DragRefresherComponent}
        />
        <div styleName="content" id={viewId.current}>
          <div className="flex" styleName="card">
            <div className="flex" styleName="card-info">
              <div styleName="card-title">代收短信</div>
              <div styleName="card-desc">开启后，家长将代收设备所有短信。</div>
            </div>
            <Switch
              styleName="border-switch"
              uncheckedIcon={false}
              checkedIcon={false}
              offColor={'#ffffff'}
              onColor={'#256eff'}
              boxShadow={'0 0 1px 1px rgba(0, 0, 0, 0.2)'}
              activeBoxShadow={'0 0 1px 1px rgba(0, 0, 0, 0.08)'}
              width={51}
              height={28}
              onChange={checked => changeConfig(checked)}
              checked={switchStatus}
            />
          </div>
          {pagination?.list && pagination?.list?.length ? (
            <div styleName="list">{renderMessages()} </div>
          ) : (
            <>
              {!pagination?.list?.length && pagination?.list !== null ? (
                <div className="flex" styleName="empty">
                  <img src={emptyUrl} alt="" styleName="empty-img" />
                  <div styleName="empty-text">暂时还没有收到短信哦</div>
                </div>
              ) : null}
            </>
          )}
          <PullRefresher
            onRefresh={loadMore}
            disabled={dataCondition.start >= pagination?.totalPage}
            ref={PullRefresherComponent}
          />
        </div>
      </div>
    </div>
  );
};

export default DeviceMessages;
